import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import {
  DateRange,
  SingleInputDateRangeField,
  PickersShortcutsProps,
  PickersShortcutsItem,
} from '@mui/x-date-pickers-pro';
import {
  subDays,
  subWeeks,
  subMonths,
  startOfMonth,
  startOfDay,
  addDays,
  startOfWeek,
  endOfDay,
} from 'date-fns';
import { Box, Chip, Divider, FormControl, List, ListItem } from '@mui/material';

import './filter.css';
import React from 'react';
import { toast } from 'react-toastify';

const shortcutsItems: PickersShortcutsItem<DateRange<Date>>[] = [
  {
    label: 'Last 7 Days',
    getValue: () => {
      const today = new Date();
      return [startOfDay(subDays(today, 7)), endOfDay(subDays(today, 1))];
    },
  },
  {
    label: 'This Month',
    getValue: () => {
      const today = new Date();
      return [startOfDay(startOfMonth(today)), today];
    },
  },
  {
    label: 'Last Week',
    getValue: () => {
      const today = new Date();
      // last sunday to saturday
      const lastSunday = subWeeks(startOfWeek(today), 1);
      return [lastSunday, endOfDay(addDays(lastSunday, 6))];
    },
  },
  {
    label: 'Last Month',
    getValue: () => {
      const today = new Date();
      return [
        startOfDay(subMonths(startOfMonth(today), 1)),
        endOfDay(subDays(startOfMonth(today), 1)),
      ];
    },
  },
  // {
  //   label: 'Reset',
  //   getValue: () => [null, null],
  // },
];

type Props = {
  storageKey: string;
  value: DateRange<Date>;
  setValue: (value: DateRange<Date>) => void;
};

function CalendarIcon() {
  return (
    <img
      src="/calendar.svg"
      alt="calendar"
      style={{ width: '14px', marginRight: '10px' }}
    />
  );
}

function CustomRangeShortcuts(props: PickersShortcutsProps<DateRange<Date>>) {
  const { items, onChange, isValid, changeImportance = 'accept' } = props;

  if (items == null || items.length === 0) {
    return null;
  }

  const resolvedItems = items.map(item => {
    const newValue = item.getValue({ isValid });

    return {
      label: item.label,
      onClick: () => {
        onChange(newValue, changeImportance, item);
      },
      disabled: !isValid(newValue),
    };
  });

  return (
    <Box
      sx={{
        gridRow: 3,
        gridColumn: 2,
        overflow: 'auto',
      }}>
      <List
        dense
        sx={theme => ({
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: theme.spacing(1),
          px: theme.spacing(2),
          '& .MuiListItem-root': {
            pt: 0,
            pl: 0,
            pr: 0,
          },
        })}>
        {resolvedItems.map(item => (
          <ListItem
            key={item.label}
            sx={{
              p: 0,
            }}>
            <Chip {...item} className="date-range-picker-shortcut" />
          </ListItem>
        ))}
      </List>
      <Divider />
    </Box>
  );
}

const INVALID_DATE_TOAST_ID = 'INVALID_DATE_TOAST_ID';

export default function MyDateRangePicker({
  storageKey,
  value,
  setValue,
}: Props) {
  const [error, setError] = React.useState(false);

  return (
    <FormControl
      error={error}
      sx={{ m: 1, width: 164 }}
      style={{ marginLeft: 0 }}>
      <DateRangePicker
        slotProps={{
          shortcuts: { items: shortcutsItems },
          actionBar: { actions: [] },
          textField: {
            placeholder: 'Select Dates',
            InputProps: {
              startAdornment: <CalendarIcon />,
            },
            InputLabelProps: { shrink: true },
            inputProps: {
              className: 'date-range-picker-input',
            },
          },
        }}
        slots={{
          shortcuts: CustomRangeShortcuts,
          field: SingleInputDateRangeField,
        }}
        value={value}
        onChange={(newValue, context) => {
          let [start, end] = newValue;
          if (start) start = startOfDay(start);
          if (end) end = endOfDay(end);
          const isInvalid = !!context.validationError?.filter(Boolean).length;
          if (isInvalid) {
            setError(true);
            // only show 1 toast at a time - otherwise it's too spammy
            if (!toast.isActive(INVALID_DATE_TOAST_ID))
              toast.error('Invalid date range', {
                toastId: INVALID_DATE_TOAST_ID,
              });
            return;
          }
          setError(false);
          setValue([start, end]);

          if (storageKey)
            sessionStorage.setItem(storageKey, JSON.stringify([start, end]));
        }}
        calendars={1}
        className="date-range-picker"
        format="MMM d"
      />
    </FormControl>
  );
}
